import { useMemo } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import toIDR from "helpers/toIDR";
import { useDetailOrder } from "api/order";

const OrderInvoiceView = () => {
    const { order_id } = useParams();
    const { data, isLoading } = useDetailOrder({ order_id });
    const order = data?.data?.data;

    const subtotal = useMemo(() => {
        return order?.order_product?.reduce((total: number, p: any) => {
            const unit = Math.floor(Number(p.sub_total));
            return total + unit * (p.qty || 0);
        }, 0) ?? 0;
    }, [order]);

    const is1212Day = () => {
        const shippingDateString = order?.shipping_date;
        if (!shippingDateString) return false;

        const date = dayjs(shippingDateString); // otomatis parse YYYY-MM-DD

        return date.date() === 12 && date.month() === 11;
        // month() === 11 karena month dimulai dari 0 (0 = Januari, 11 = Desember)
    };

    if (isLoading) {
        return <div className="p-6 text-center">Loading invoice...</div>;
    }

    if (!order) {
        return <div className="p-6 text-center text-red-500">Order tidak ditemukan</div>;
    }

    return (
        <div className="max-w-lg mx-auto p-5 bg-white min-h-screen">

            {/* HEADER */}
            <div className="text-center mb-8">
                <img
                    src="/sayursembalun-logo.png"
                    alt="Logo"
                    className="w-28 mx-auto mb-3"
                />
                <h1 className="text-2xl font-bold text-gray-800">
                    Invoice SS-{order.id}
                </h1>
                <p className="text-gray-500">
                    {dayjs(order.created_at).format("DD MMMM YYYY")}
                </p>
            </div>

            {/* PELANGGAN */}
            <div className="mb-6">
                <h2 className="font-semibold mb-2 text-gray-700">Informasi Pelanggan</h2>
                <div className="p-4 bg-gray-50 rounded-lg border">
                    <div className="font-medium text-gray-800">{order.customer_name}</div>
                    <div className="text-gray-600">{order.customer_phone}</div>
                </div>
            </div>

            {/* ALAMAT */}
            {/* <div className="mb-6">
                <h2 className="font-semibold mb-2 text-gray-700">Alamat Pengiriman</h2>
                <div className="p-4 bg-gray-50 rounded-lg border">
                    <div className="text-gray-700">{order.user_detail?.full_address}</div>
                    <div className="text-gray-700">
                        {order.user_detail?.village}, {order.user_detail?.district}, {order.user_detail?.city}
                    </div>
                    <div className="text-gray-700">NUSA TENGGARA BARAT, INDONESIA</div>
                </div>
            </div> */}

            {/* WAKTU + CATATAN */}
            {/* <div className="mb-6 space-y-3">
                <div>
                    <h2 className="font-semibold mb-1 text-gray-700">Waktu Pengiriman</h2>
                    <p className="text-gray-700 border p-3 bg-gray-50 rounded-lg">
                        {moment(order.shipping_date).format("DD MMM YYYY")}, {order.shipping_time}
                    </p>
                </div>

                <div>
                    <h2 className="font-semibold mb-1 text-gray-700">Catatan</h2>
                    <p className="text-gray-700 border p-3 bg-gray-50 rounded-lg">
                        {order.notes || "-"}
                    </p>
                </div>
            </div> */}

            {/* METODE PEMBAYARAN */}
            {/* <div className="mb-6">
                <h2 className="font-semibold mb-1 text-gray-700">Metode Pembayaran</h2>
                <div className="p-4 bg-gray-50 border rounded-lg space-y-2">
                    <div className="font-medium">
                        {order.payment_method === "cash" ? "Tunai" : "Transfer"}
                    </div>

                    {order.payment_method !== "cash" && (
                        <div className="text-sm text-gray-700 space-y-1">
                            <p>Transfer Pembayaran:</p>
                            <p>7255024949 (BCA)</p>
                            <p>1610027879381 (Mandiri)</p>
                            <p>Atas Nama: Wisnu Almi Pradya Sa'bani</p>
                        </div>
                    )}
                </div>
            </div> */}

            {/* PRODUK */}
            <div className="mb-6">
                <h2 className="font-semibold mb-2 text-gray-700">Detail Pesanan</h2>
                <div className="space-y-4">

                    {order.order_product?.map((p: any, idx: number) => {
                        const total = p.qty * Number(p.sub_total);
                        return (
                            <div
                                key={idx}
                                className="flex justify-between border-b pb-3"
                            >
                                <div>
                                    <div className="font-medium text-gray-800">
                                        {p.product?.name} (x{p.qty})
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {p.variant?.weight} {p.variant?.satuan}
                                    </div>
                                </div>

                                <div className="font-semibold text-gray-900">
                                    {toIDR(total)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* TOTAL */}
            <div className="mt-6 bg-gray-50 border p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span>
                    <span>{toIDR(subtotal)}</span>
                </div>

                <div className="flex justify-between text-gray-700">
                    <span>Biaya Pengiriman</span>
                    <span>{toIDR(order.initial_shipping_cost)}</span>
                </div>

                {order.discount_day > 0 && is1212Day ? (
                    <div className="flex justify-between text-gray-700">
                        <span>Diskon 10% Hari Konsumen</span>
                        <span>-{toIDR(order.discount_day)}</span>
                    </div>
                ) : (
                    <div className="flex justify-between text-gray-700">
                        <span>Diskon 7% Hari Rabu</span>
                        <span>-{toIDR(order.discount_day)}</span>
                    </div>
                )}

                <div className="flex justify-between text-gray-700">
                    <span>Diskon</span>
                    <span>-{toIDR(order.discount_fee)}</span>
                </div>

                <div className="flex justify-between text-gray-700">
                    <span>Diskon Biaya Pengiriman</span>
                    <span>-{toIDR(order.subsidy_shipping_cost)}</span>
                </div>

                <div className="flex justify-between text-lg font-bold border-t pt-3 text-gray-900">
                    <span>Total</span>
                    <span>{toIDR(order.total_price)}</span>
                </div>
            </div>

            <p className="text-center text-xs text-gray-400 mt-6">
                Terima kasih telah berbelanja bersama kami!
            </p>
        </div>
    );
};

export default OrderInvoiceView;
